"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAppDispatch } from "@/store/hooks";
import { fetchCurrentUser } from "@/store/slices/authSlice";
import { Shield, User, Lock, ArrowRight, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { MaintenanceBanner } from "@/app/components/ui/MaintenanceBanner";

export default function Home() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});
  const [message, setMessage] = useState<{ type: "error" | "success"; text: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if user is already logged in (proxy will handle final auth check)
  useEffect(() => {
    setIsChecking(false);
  }, []);

  useEffect(() => {
    if (isChecking) return;

    try {
      // Try localStorage first (username + password)
      const savedUser = localStorage.getItem("aiyappa_remember_username");
      const savedPass = localStorage.getItem("aiyappa_remember_password");
      if (savedUser) {
        setUsername(savedUser);
        if (savedPass) setPassword(savedPass);
        setRemember(true);
        return;
      }

      // Fallback: check cookie for remembered username
      if (typeof document !== 'undefined') {
        const match = document.cookie.match(/(?:^|; )aiyappa_remember_username=([^;]+)/);
        if (match) {
          setUsername(decodeURIComponent(match[1]));
          setRemember(true);
        }
      }
    } catch (e) {
      // ignore
    }
  }, [isChecking]);

  function validate() {
    const errs: { username?: string; password?: string } = {};
    if (!username || username.trim().length === 0) errs.username = "Username is required";
    if (!password || password.length < 6) errs.password = "Password is required (min 6 chars)";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    if (!validate()) return;

    setLoading(true);

    try {
      // Store remember preference
      try {
        if (remember) {
          // Store username + password in localStorage for prefill.
          // Note: Storing passwords in localStorage is insecure; consider using a secure token instead.
          localStorage.setItem("aiyappa_remember_username", username);
          localStorage.setItem("aiyappa_remember_password", password);
          // Also set a non-http-only cookie for username as a fallback (30 days)
          if (typeof document !== 'undefined') {
            document.cookie = `aiyappa_remember_username=${encodeURIComponent(username)}; path=/; max-age=${30 * 24 * 60 * 60}`;
          }
        } else {
          localStorage.removeItem("aiyappa_remember_username");
          localStorage.removeItem("aiyappa_remember_password");
          if (typeof document !== 'undefined') {
            document.cookie = 'aiyappa_remember_username=; path=/; max-age=0';
          }
        }
      } catch (err) {
        // ignore storage errors
      }

      // Call authentication API
      const response = await fetch("/api/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      if (!response.ok) {
        setMessage({ type: "error", text: data.error || "Authentication failed" });
        setLoading(false);
        return;
      }

      setMessage({ type: "success", text: "Login successful! Redirecting..." });
      setPassword("");

      dispatch(fetchCurrentUser());

      setTimeout(() => {
        router.push("/dashboard");
      }, 500);
    } catch (error) {
      console.error("Login error:", error);
      setMessage({ type: "error", text: "An error occurred. Please try again." });
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-zinc-950 px-4">
      {/* Ambient gradient orbs */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />

      {/* Maintenance banner */}
      <MaintenanceBanner expectedBackDate="1 September 2026" />

      {isChecking ? (
        <div className="relative text-center">
          <p className="text-zinc-400 text-sm">Loading...</p>
        </div>
      ) : (
        <main className="relative w-full max-w-md bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl shadow-2xl p-8">
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-600/30 mb-4">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-extrabold tracking-tight text-zinc-50">Aiyappa HRMS</h1>
            <p className="mt-1 text-xs text-zinc-400">Sign in to your account</p>
          </div>

          {message && (
            <div
              className={`mt-6 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
                message.type === "error"
                  ? "bg-red-500/10 border-red-500/20 text-red-400"
                  : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
              }`}
            >
              {message.type === "error" ? (
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
              ) : (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-zinc-300 mb-1.5">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-zinc-800 bg-zinc-950/80 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter username"
                  aria-invalid={!!errors.username}
                />
              </div>
              {errors.username && <p className="mt-1 text-xs text-red-400">{errors.username}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-zinc-300 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-zinc-800 bg-zinc-950/80 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter password"
                  aria-invalid={!!errors.password}
                />
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-400">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between">
              <label className="inline-flex items-center text-xs text-zinc-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="mr-2 h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-950 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0"
                />
                Remember me
              </label>
              <Link href="/reset-password" className="text-xs text-zinc-400 hover:text-indigo-400 font-semibold">
                Forgot / Reset password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600 disabled:opacity-60 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-600/30 active:scale-[0.98] transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </main>
      )}
    </div>
  );
}
